import WidgetModel from '../widget-model'
import SpeedometerView from './speedometer-view'
import _ from 'lodash'
import config from 'scoreboard/components/config'
import i18n from 'scoreboard/components/i18n'
import constants from 'scoreboard/components/constants'
import BaseGaugeModel from '../base-gauge-model'

var SpeedometerModel = BaseGaugeModel.extend({
	// If these change, update WIDGET_DIMENSIONS in DashboardBuilder.kit
	containerDefaults: {
		width: 180
	},

	viewCls: SpeedometerView,

	type: 'speedometer',

	gaugeOptions: function({ datasetQueryResponse } = {}) {
		var props = this.properties.toJSON()
		var data = this.relevantData()

		let baseOptions = BaseGaugeModel.prototype.gaugeOptions.apply(this, arguments)
		let opt = _.extend(baseOptions,{
			model: data.scorecardNode,
			section: props.section,
			// #19426 #21314 don't show messages like "Not updated for this period"
			// if none of the options for "above the speedometer" are turned on,
			// because it changes the height of the widget
			hideExplanation: !(props.showActual || props.showChange)
		})

		opt.thresholds = props.thresholdMode
		opt.value = props.showActual
		// most of this.relevantData() should be relevant, so let's use it
		// as defaults for the options
		opt = _.defaults(opt, data)

		if (!props.showChange) {
			opt.previousScore = undefined
		}
		return opt
	},

	/**
	 * Returns the valid threshold configuration modes for the speedometer's
	 * scorecard item
	 */
	validThresholdModes() {
		if (!this.metricish()) {
			return []
		}

		let scoringType = config.scoringType(this.scorecardItem().scoringTypeId)

		if (_.get(scoringType, 'standardScoringType') === constants.YES_NO ||
			_.get(scoringType, 'standardScoringType') === constants.UNSCORED ||
			scoringType.isCustomText /* #50269 */) {
			return []
		}

		let modes = [ { value: 'none', label: i18n.get('common.none') } ]
		let defaultModeLabel

		switch (_.get(scoringType, 'standardScoringType')) {
			case constants.GOAL_RED_FLAG:
			case constants.THREE_COLOR:
			case constants.FOUR_COLOR_BLUE:
			case constants.FOUR_COLOR_ORANGE:
			case constants.SCORED_MIDDLE_4_COLOR:
				// These scoring types all have Red Flag and Goal thresholds, so
				// use those as the default
				defaultModeLabel = `${i18n.get('scorecards.node.metric.threshold.goal')}/${i18n.get('scorecards.node.metric.threshold.base')}`
				break
			case constants.GOAL_ONLY:
			case constants.TWO_COLOR:
				// These scoring types don't have a clear "bad" threshold for the left,
				// so we just display the Goal threshold
				defaultModeLabel = i18n.get('scorecards.node.metric.threshold.goal')
				break
			case constants.STABILIZE_2:
			case constants.STABILIZE_3:
				// Stabilize metrics have two sets of "bad" thresholds on either
				// side of the goal, so it's not clear what to put in the left
				// spot. So we just display the Best threshold
				defaultModeLabel = i18n.get('scorecards.node.metric.threshold.best')
				break
		}

		modes.push({ value: 'default', label: defaultModeLabel })

		switch (_.get(scoringType, 'standardScoringType')) {
			case constants.THREE_COLOR:
			case constants.FOUR_COLOR_BLUE:
			case constants.FOUR_COLOR_ORANGE:
				// #20917 give worst/best option for metric types that have those thresholds
				modes.push({
					value: 'best-worst',
					label: `${i18n.get('scorecards.node.metric.threshold.best')}/${i18n.get('scorecards.node.metric.threshold.worst')}`
				})
				break
		}

		return modes
	},

	considerProperties: function (props) {
		var safe = _.extend({}, props)

		if (safe.thresholdMode) {
			// The set of valid threshold modes depends on the metric's scoring type.
			// Before setting this speedometer to the same value, we need
			// to make sure it is valid for this speedometer's metric
			let validModes = this.validThresholdModes().map(m => m.value)
			if (!_.includes(validModes, safe.thresholdMode)) {
				delete safe.thresholdMode
			}
		}

		return WidgetModel.prototype.considerProperties.call(this, safe)
	},

	/**
	 * @override
	 */
	validShowProperties: function () {
		let props = BaseGaugeModel.prototype
			.validShowProperties.apply(this, arguments)

		// Shows for most data sources, but not non-metric scorecard items
		if (this.metricish() ||
			['datasets', 'initiatives'].includes(this.section())) {
			props.push({
				// this used to be called "KPI Value", and before that "Actual
				// Value", so that's where the property name of "showActual"
				// comes from
				property: 'showActual',
				label: 'dashboards.dashboard.speedometer.chartedValue'
			})
		}

		return props
	},

	multiSettableProps: function () {
		return _.map(this.validShowProperties(), 'property')
			.concat([ 'background', 'thresholdMode', 'calendarId', 'periodId', 'periodDelta' ])
	}
})

export default SpeedometerModel

SpeedometerModel.__filename__ = 'dashboards/widgets/speedometer/speedometer-model.js'
